import React, { memo, useState, useCallback } from 'react';
import { X, Flame, BookOpen, Target, Clock, TrendingUp, Moon, Timer, Eye, ChevronDown, Heart, Music, Headphones, Repeat } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface ReadingJourneyModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentStreak: number;
  totalBooksRead: number;
  readingGoal: number;
  booksThisMonth: number;
  onUpdateGoal: (goal: number) => void;
  readingConsistency7d?: number;
  sessionsToday?: number;
  // Extended stats
  totalReadingTime?: number;
  longestSession?: number;
  nightReadsThisWeek?: number;
  scenesExperienced?: number;
  readingPace?: string;
  // NEW: Enhanced stats
  favoriteBook?: { id: number; title: string; sessions: number } | null;
  mostRepeats?: number;
  totalListeningMinutes?: number;
  likedTracksCount?: number;
  likedAudiobooksCount?: number;
}

// Compact stat pill component
const StatPill = memo(({ 
  icon: Icon, 
  value, 
  label, 
  color 
}: { 
  icon: React.ElementType; 
  value: string | number; 
  label: string; 
  color: string;
}) => (
  <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
    <div className={cn("w-10 h-10 rounded-full flex items-center justify-center", color)}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <span className="text-lg font-semibold text-white">{value}</span>
    <span className="text-[11px] text-white/60 uppercase tracking-wide">{label}</span>
  </div>
));
StatPill.displayName = 'StatPill';

// Large progress ring - the hero element
const HeroProgressRing = memo(({ 
  booksRead, 
  goal 
}: { 
  booksRead: number; 
  goal: number;
}) => {
  const progressPercentage = goal > 0 ? (booksRead / goal) * 100 : 0;
  const goalComplete = booksRead >= goal && goal > 0;
  const booksRemaining = Math.max(0, goal - booksRead);
  
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const progressOffset = circumference - (circumference * Math.min(progressPercentage, 100)) / 100;

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg className="w-44 h-44 transform -rotate-90">
          {/* Background circle */}
          <circle
            cx="88"
            cy="88"
            r={radius}
            stroke="rgba(255,255,255,0.15)"
            strokeWidth="14"
            fill="none"
          />
          {/* Progress circle */}
          <circle
            cx="88"
            cy="88"
            r={radius}
            stroke={goalComplete ? "#FFD60A" : "#34C759"}
            strokeWidth="14"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={progressOffset}
            style={{ 
              filter: `drop-shadow(0 0 12px ${goalComplete ? 'rgba(255,214,10,0.6)' : 'rgba(52,199,89,0.6)'})`,
              transition: 'stroke-dashoffset 0.5s ease-out'
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-bold text-white">
            {Math.round(Math.min(progressPercentage, 100))}%
          </span>
          <span className="text-xs text-white/50 uppercase tracking-wider mt-1">
            {goalComplete ? 'Complete!' : 'Progress'}
          </span>
        </div>
      </div>
      
      {/* Books read / remaining */}
      <div className="flex items-center gap-8 mt-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-white">{booksRead}</p>
          <p className="text-xs text-white/60 uppercase tracking-wide">Read</p>
        </div>
        <div className="w-px h-10 bg-white/20" />
        <div className="text-center">
          <p className={cn("text-2xl font-bold", goalComplete ? "text-yellow-400" : "text-white")}>
            {goalComplete ? '🎉' : booksRemaining}
          </p>
          <p className="text-xs text-white/60 uppercase tracking-wide">
            {goalComplete ? 'Done!' : 'To Go'}
          </p>
        </div>
      </div>
    </div>
  );
});
HeroProgressRing.displayName = 'HeroProgressRing';

// Inline goal setter
const GoalSetter = memo(({ 
  goal, 
  onUpdateGoal 
}: { 
  goal: number; 
  onUpdateGoal: (goal: number) => void;
}) => {
  const [localGoal, setLocalGoal] = useState(goal);
  
  const handleChange = useCallback((values: number[]) => {
    const newGoal = values[0];
    setLocalGoal(newGoal);
  }, []);
  
  const handleCommit = useCallback((values: number[]) => {
    onUpdateGoal(values[0]);
  }, [onUpdateGoal]);

  return (
    <div className="w-full max-w-xs mx-auto">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-white/70">Monthly Goal</span>
        <span className="text-lg font-semibold text-white">{localGoal} books</span>
      </div>
      <Slider
        value={[localGoal]}
        min={1}
        max={30}
        step={1}
        onValueChange={handleChange}
        onValueCommit={handleCommit}
        className="w-full"
      />
      <div className="flex justify-between mt-2 text-xs text-white/40">
        <span>1</span>
        <span>30</span>
      </div>
    </div>
  );
});
GoalSetter.displayName = 'GoalSetter';

// Detailed stats section
const DetailedStats = memo(({
  totalReadingTime = 0,
  longestSession = 0,
  nightReadsThisWeek = 0,
  scenesExperienced = 0,
  readingPace = 'Chill pace',
  favoriteBook = null,
  mostRepeats = 0,
  totalListeningMinutes = 0,
  likedTracksCount = 0,
  likedAudiobooksCount = 0,
}: {
  totalReadingTime?: number;
  longestSession?: number;
  nightReadsThisWeek?: number;
  scenesExperienced?: number;
  readingPace?: string;
  favoriteBook?: { id: number; title: string; sessions: number } | null;
  mostRepeats?: number;
  totalListeningMinutes?: number;
  likedTracksCount?: number;
  likedAudiobooksCount?: number;
}) => {
  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <div className="space-y-4 w-full max-w-sm">
      {/* Favorite Book Banner */}
      {favoriteBook && (
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Heart className="w-4 h-4 text-pink-400" />
            <span className="text-xs text-white/60 uppercase tracking-wider">Favorite Book</span>
          </div>
          <p className="text-white font-semibold text-sm truncate">{favoriteBook.title}</p>
          <p className="text-xs text-white/50 mt-1">Read {favoriteBook.sessions} times</p>
        </div>
      )}

      {/* Reading Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <Timer className="w-5 h-5 text-blue-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-white">{formatTime(totalReadingTime)}</p>
          <p className="text-[10px] text-white/50 uppercase">Reading Time</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <Clock className="w-5 h-5 text-green-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-white">{formatTime(longestSession)}</p>
          <p className="text-[10px] text-white/50 uppercase">Longest Session</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <Moon className="w-5 h-5 text-indigo-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-white">{nightReadsThisWeek}</p>
          <p className="text-[10px] text-white/50 uppercase">Night Reads</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <Eye className="w-5 h-5 text-pink-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-white">{scenesExperienced}</p>
          <p className="text-[10px] text-white/50 uppercase">Scenes</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <Repeat className="w-5 h-5 text-orange-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-white">{mostRepeats}</p>
          <p className="text-[10px] text-white/50 uppercase">Most Repeats</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <Headphones className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-white">{formatTime(totalListeningMinutes)}</p>
          <p className="text-[10px] text-white/50 uppercase">Listening</p>
        </div>
      </div>

      {/* Liked Content */}
      {(likedTracksCount > 0 || likedAudiobooksCount > 0) && (
        <div className="flex items-center justify-center gap-6 pt-2">
          {likedTracksCount > 0 && (
            <div className="flex items-center gap-2 text-white/60">
              <Music className="w-4 h-4 text-green-400" />
              <span className="text-sm">{likedTracksCount} tracks</span>
            </div>
          )}
          {likedAudiobooksCount > 0 && (
            <div className="flex items-center gap-2 text-white/60">
              <Heart className="w-4 h-4 text-red-400" />
              <span className="text-sm">{likedAudiobooksCount} audiobooks</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
DetailedStats.displayName = 'DetailedStats';

export const ReadingJourneyModal = memo(({
  isOpen,
  onClose,
  currentStreak,
  totalBooksRead,
  readingGoal,
  booksThisMonth,
  onUpdateGoal,
  readingConsistency7d = 0,
  sessionsToday = 0,
  totalReadingTime = 0,
  longestSession = 0,
  nightReadsThisWeek = 0,
  scenesExperienced = 0,
  readingPace = 'Chill pace',
  favoriteBook = null,
  mostRepeats = 0,
  totalListeningMinutes = 0,
  likedTracksCount = 0,
  likedAudiobooksCount = 0,
}: ReadingJourneyModalProps) => {
  const isMobile = useIsMobile();
  const [showDetails, setShowDetails] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className={cn(
          "max-w-none max-h-none p-0 border-0 bg-black/98",
          isMobile ? "w-full h-full rounded-none" : "w-[420px] h-auto max-h-[90vh] rounded-2xl"
        )}
      >
        {/* Close button - must be above ScrollArea */}
        <button
          onClick={onClose}
          className="absolute z-50 w-10 h-10 rounded-full bg-white/10 backdrop-blur-[20px] border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
          style={{
            top: isMobile ? 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' : '1.5rem',
            right: '1rem'
          }}
          aria-label="Close"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Content with ScrollArea for short screens */}
        <ScrollArea className={cn(
          "w-full",
          isMobile ? "h-[100dvh]" : "max-h-[90vh]"
        )}>
          <div className={cn(
            "flex flex-col items-center px-6",
            isMobile ? "pt-24 pb-24 gap-6" : "py-10 gap-5"
          )}>
            {/* Header */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-1">Your Reading Journey</h2>
              <p className="text-sm text-white/50">This month's progress</p>
            </div>

            {/* Hero Progress Ring */}
            <HeroProgressRing booksRead={booksThisMonth} goal={readingGoal} />

            {/* Quick Stats Row */}
            <div className="flex items-start justify-center gap-6 w-full max-w-sm">
              <StatPill 
                icon={Flame} 
                value={currentStreak} 
                label="Streak" 
                color="bg-orange-500/80" 
              />
              <StatPill 
                icon={BookOpen} 
                value={totalBooksRead} 
                label="Total" 
                color="bg-blue-500/80" 
              />
              <StatPill 
                icon={TrendingUp} 
                value={`${Math.round(readingConsistency7d)}%`} 
                label="7-Day" 
                color="bg-green-500/80" 
              />
              <StatPill 
                icon={Clock} 
                value={sessionsToday} 
                label="Today" 
                color="bg-purple-500/80" 
              />
            </div>

            {/* Goal Setter */}
            <div className="w-full max-w-sm pt-4 border-t border-white/10">
              <GoalSetter goal={readingGoal} onUpdateGoal={onUpdateGoal} />
            </div>

            {/* Expandable Detailed Stats */}
            <div className="w-full max-w-sm">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center justify-center gap-2 w-full py-2 text-sm text-white/60 hover:text-white/80 transition-colors"
              >
                <span>{showDetails ? 'Hide' : 'Show'} detailed stats</span>
                <ChevronDown className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  showDetails && "rotate-180"
                )} />
              </button>
              
              {showDetails && (
                <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-200">
                  <DetailedStats
                    totalReadingTime={totalReadingTime}
                    longestSession={longestSession}
                    nightReadsThisWeek={nightReadsThisWeek}
                    scenesExperienced={scenesExperienced}
                    readingPace={readingPace}
                    favoriteBook={favoriteBook}
                    mostRepeats={mostRepeats}
                    totalListeningMinutes={totalListeningMinutes}
                    likedTracksCount={likedTracksCount}
                    likedAudiobooksCount={likedAudiobooksCount}
                  />
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
});

ReadingJourneyModal.displayName = 'ReadingJourneyModal';

export default ReadingJourneyModal;
